import { Fragment, useEffect } from 'react';

import { observer } from 'mobx-react-lite';

import { Alert } from '@components/alert';
import { Checkbox } from '@components/checkbox';
import { DatePicker } from '@components/datepicker';
import { FormLabel } from '@components/form-label';
import { IconFormIndicatorError } from '@components/icons/form-indicator-error';
import { IconFormIndicatorInfo } from '@components/icons/form-indicator-info';
import { IconFormIndicatorPending } from '@components/icons/form-indicator-pending';
import { IconFormIndicatorSuccess } from '@components/icons/form-indicator-success';
import { IconOpen } from '@components/icons/open';
import { IconTextFormatLink } from '@components/icons/text-format-link';
import { Select } from '@components/select';
import { SelectMulti } from '@components/selectmulti';
import { Separator } from '@components/separator';
import { Stack } from '@components/stack';
import { Switch } from '@components/switch';
import { Text } from '@components/text';
import { TextField } from '@components/text-field';
import { TextArea } from '@components/textarea';

import { useModal } from 'features/modals';
import { useStore } from 'models/Provider';

import { File, RepeatingContainer } from './styles';

function renderInput(data, validator, refetch, showFullscreenModal, isDisabled) {
  switch (data.type) {
    case 'header':
      return <Text font="bold18" color="purple200" text={data.value} />;
    case 'separator':
      return <Separator />;
    case 'alert':
      return <Alert variant={data.alertType ?? 'error'}>{data.value}</Alert>;
    case 'string':
    case 'derived':
      return (
        <TextField
          label={data.required ? `* ${data.label}` : data.label}
          labelIcon={data.source && <RenderLinkedSource source={data.source} />}
          onChange={data.setValue}
          value={data.value || ''}
          error={data.error}
          data-id={data.name}
          isDisabled={isDisabled}
          onBlur={(e) => {
            validator && validator(e.target.value, data);
            data.dynamic && refetch(data.name);
          }}
        />
      );
    case 'text':
      return (
        <TextArea
          label={data.required ? `* ${data.label}` : data.label}
          labelIcon={data.source ? <RenderLinkedSource source={data.source} /> : undefined}
          id={data.name}
          onChange={data.setValue}
          value={data.value || ''}
          error={data.error}
          isDisabled={isDisabled}
          data-id={data.name}
          onBlur={(e) => {
            validator && validator(e.target.value, data);
            data.dynamic && refetch(data.name);
          }}
        />
      );
    case 'email':
      return (
        <TextField
          label={data.required ? `* ${data.label}` : data.label}
          labelIcon={data.source && <RenderLinkedSource source={data.source} />}
          onChange={data.setValue}
          type="email"
          value={data.value}
          error={data.error}
          isDisabled={isDisabled}
          data-id={data.name}
          onBlur={(e) => {
            validator && validator(e.target.value, data);
            data.dynamic && refetch(data.name);
          }}
        />
      );
    case 'boolean':
      return (
        <Stack space={1}>
          <FormLabel
            htmlFor={data.name}
            text={data.required ? `* ${data.label}` : data.label}
            icon={data.source && <RenderLinkedSource source={data.source} />}
          />
          <Switch
            defaultValue={data.value}
            id={data.name}
            name={data.name}
            data-id={data.name}
            isDisabled={isDisabled}
            onChange={(val) => {
              data.setValue(val);
              data.dynamic && refetch(data.name);
            }}
          />
        </Stack>
      );
    case 'checkbox':
      return (
        <Stack space={1}>
          <FormLabel
            htmlFor={data.name}
            text={data.required ? `* ${data.label}` : data.label}
            icon={data.source && <RenderLinkedSource source={data.source} />}
          />
          <Checkbox
            value={data.value ?? false}
            id={data.name}
            name={data.name}
            data-id={data.name}
            isDisabled={isDisabled}
            label={data.checkboxLabel}
            onChange={() => {
              data.setValue(!data.value);
              data.dynamic && refetch(data.name);
            }}
          />
        </Stack>
      );
    case 'choices':
    case 'inlinechoices':
    case 'autocomplete':
      return (
        <Select
          label={data.required ? `* ${data.label}` : data.label}
          labelIcon={data.source && <RenderLinkedSource source={data.source} />}
          options={data.choicesToObj}
          onChange={(value) => {
            data.setValue(value);
            data.dynamic && refetch(data.name);
          }}
          key={data.value}
          defaultValue={data.value !== undefined && data.value !== null ? String(data.value) : null}
          error={data.error}
          isDisabled={isDisabled}
          dataId={data.name}
        />
      );
    case 'multiple':
    case 'inlinemultiplechoices':
      return (
        <SelectMulti
          label={data.required ? `* ${data.label}` : data.label}
          labelIcon={data.source && <RenderLinkedSource source={data.source} />}
          options={data.choices.map((choice) => {
            return { value: choice[0], label: choice[1] };
          })}
          onChange={(value) => {
            data.setValue(value);
            data.dynamic && refetch(data.name);
          }}
          defaultValue={Array.isArray(data.value) ? data.value : []}
          error={data.error}
          isDisabled={isDisabled}
          dataId={data.name}
        />
      );
    case 'date':
      return (
        <Stack space={1}>
          <FormLabel
            htmlFor={data.name}
            text={data.required ? `* ${data.label}` : data.label}
            icon={data.source && <RenderLinkedSource source={data.source} />}
          />
          <DatePicker
            value={data.value ? new Date(data.value) : null}
            onChange={data.setValue}
            error={data.error}
            isDisabled={isDisabled}
            data-id={data.name}
          />
        </Stack>
      );
    case 'file':
      return (
        <File onClick={() => showFullscreenModal('INTEGRATION_FILE_PREVIEW', { data })}>
          <Stack align="center" orientation="horizontal" space={2} style={{ overflow: 'hidden' }}>
            <IconOpen />
            <Text
              color="purple100"
              font="semibold14"
              text={data.required ? `* ${data.label}` : data.label}
            />
          </Stack>
        </File>
      );
    default:
      return null;
  }
}

function renderRepeatingInput(data) {
  return data.fields.map((fieldSet, idx) => (
    <RepeatingContainer key={idx}>
      {fieldSet.map((fs, fsIdx) => (
        <Fragment key={fsIdx}>{renderInput(fs)}</Fragment>
      ))}
    </RepeatingContainer>
  ));
}

function getLinkedSource(source, t) {
  switch (source) {
    case 'application':
      return t('application.onboarding.integrations.linked.application');
    default:
      return source;
  }
}

function getStatusColor(status) {
  switch (status) {
    case 'success':
      return 'green200';
    case 'pending':
      return 'orange200';
    case 'info':
      return 'blue100';
    case 'error':
      return 'red100';
    default:
      return 'purple100';
  }
}

function statusToIcon(status) {
  switch (status) {
    case 'success':
      return <IconFormIndicatorSuccess />;
    case 'pending':
      return <IconFormIndicatorPending />;
    case 'error':
      return <IconFormIndicatorError />;
    case 'info':
    default:
      return <IconFormIndicatorInfo />;
  }
}

export const IntegrationField = observer(
  ({ data, validator, refetch, readOnly = false, isDisabled = false }) => {
    const { t } = useStore();
    const { showFullscreenModal } = useModal();
    useEffect(() => {
      validator && validator(data.value, data, t);
    }, [data, validator, t]);

    if (data.type === 'hidden') return null;

    if (readOnly) {
      return (
        <Stack space={1}>
          <Text
            color="purple100"
            font="semibold14"
            text={data.required ? `* ${data.label}` : data.label}
          />
          <Stack space={2} align="center" orientation="horizontal">
            {data.status && statusToIcon(data.status)}
            <Text color={getStatusColor(data.status)} text={data.value} />
          </Stack>
        </Stack>
      );
    }
    return (
      <Stack space={2}>
        {data.type !== 'repeating'
          ? renderInput(data, validator, refetch, showFullscreenModal, isDisabled)
          : renderRepeatingInput(data)}
        {data.fileUrl && (
          <File
            onClick={() => showFullscreenModal('CUSTOM_FILE_PREVIEW', { filePath: data.fileUrl })}
          >
            <Stack align="center" orientation="horizontal" space={2} style={{ overflow: 'hidden' }}>
              <IconOpen />
              <Text color="purple100" font="semibold14" text={data.fileName} />
            </Stack>
          </File>
        )}
      </Stack>
    );
  }
);

function RenderLinkedSource({ source }) {
  const { t } = useStore();
  return (
    <IconTextFormatLink
      tooltip={t(
        'application.onboarding.integrations.linked',
        getLinkedSource(source.integration, t)
      )}
    />
  );
}
